import React, { useMemo } from 'react';
import { graphql, useFragment, useMutation } from 'react-relay';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { TimePicker } from '@atlaskit/datetime-picker';
import { Inline, Box, xcss } from '@atlaskit/primitives';
import Select from '@atlaskit/select';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useShowErrorFlag } from '@atlassian/jira-personal-settings-error-flag/src/ui/index.tsx';
import {
	fireOperationalAnalytics,
	fireTrackAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import type {
	JiraBatchWindowPreference,
	batchWindow_personalSettingsPreferenceFields_BatchWindowPreferenceField$key,
} from '@atlassian/jira-relay/src/__generated__/batchWindow_personalSettingsPreferenceFields_BatchWindowPreferenceField.graphql';
import type { batchWindow_personalSettingsPreferenceFields_BatchWindowPreferenceFieldMutation } from '@atlassian/jira-relay/src/__generated__/batchWindow_personalSettingsPreferenceFields_BatchWindowPreferenceFieldMutation.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import messages from './messages.tsx';

type optionBatchWindow = {
	label: string;
	value: JiraBatchWindowPreference;
};

export const BatchWindowPreferenceField = ({
	batchWindowFragment,
}: {
	batchWindowFragment: batchWindow_personalSettingsPreferenceFields_BatchWindowPreferenceField$key;
}) => {
	const { formatMessage } = useIntl();
	const cloudId = useCloudId();
	const { showErrorFlag } = useShowErrorFlag();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const data = useFragment(
		graphql`
			fragment batchWindow_personalSettingsPreferenceFields_BatchWindowPreferenceField on JiraNotificationOptions {
				dailyBatchLocalTime
				batchWindow @required(action: THROW)
				id @required(action: THROW)
			}
		`,
		batchWindowFragment,
	);

	const [commit] =
		useMutation<batchWindow_personalSettingsPreferenceFields_BatchWindowPreferenceFieldMutation>(
			graphql`
				mutation batchWindow_personalSettingsPreferenceFields_BatchWindowPreferenceFieldMutation(
					$cloudId: ID!
					$input: JiraUpdateNotificationOptionsInput!
				) {
					jira {
						updateGlobalNotificationOptions(cloudId: $cloudId, input: $input)
							@optIn(to: "JiraNotificationPreference") {
							success
							errors {
								message
							}
							options {
								...batchWindow_personalSettingsPreferenceFields_BatchWindowPreferenceField
							}
						}
					}
				}
			`,
		);
	const batchWindowOptions: optionBatchWindow[] = useMemo(
		() => [
			{ label: formatMessage(messages.optionTextDef), value: 'DEFAULT_BATCHING' },
			{ label: formatMessage(messages.optionTextTenMins), value: 'TEN_MINUTES' },
			{ label: formatMessage(messages.optionTextThirtyMins), value: 'THIRTY_MINUTES' },
			{ label: formatMessage(messages.optionTextSixtyMins), value: 'ONE_HOUR' },
			{ label: formatMessage(messages.optionTextOncePerDayAt), value: 'ONCE_PER_DAY' },
		],
		[formatMessage],
	);

	const handleDailyBatchLocalTimeChange = (optionValue: string) => {
		commit({
			variables: {
				cloudId,
				input: {
					batchWindow: 'ONCE_PER_DAY',
					dailyBatchLocalTime: optionValue,
				},
			},
			optimisticResponse: {
				jira: {
					updateGlobalNotificationOptions: {
						success: true,
						errors: [],
						options: {
							id: data.id,
							batchWindow: 'ONCE_PER_DAY',
							dailyBatchLocalTime: optionValue,
						},
					},
				},
			},
			onError: (error) => {
				showErrorFlag();
				fireErrorAnalytics({
					meta: {
						id: 'mutateDailyBatchLocalTimePreference',
						packageName: 'jiraPersonalSettingsPreferenceFields',
						teamName: 'jira-notifications-platform',
					},
					error,
					sendToPrivacyUnsafeSplunk: true,
				});
			},
			onCompleted: (response) => {
				const errors = response?.jira?.updateGlobalNotificationOptions?.errors;
				if (errors && errors?.length > 0) {
					showErrorFlag();
					errors.forEach((error) => {
						fireErrorAnalytics({
							meta: {
								id: 'mutateDailyBatchLocalTimeCompletedError',
								packageName: 'jiraPersonalSettingsPreferenceFields',
								teamName: 'jira-notifications-platform',
							},
							error: new Error(error?.message || 'Unknown Error'),
							sendToPrivacyUnsafeSplunk: true,
						});
					});
				} else {
					fireTrackAnalytics(
						createAnalyticsEvent({}),
						'dailyBatchLocalTime changed',
						'dailyBatchLocalTime',
						{
							value: optionValue,
						},
					);
					fireOperationalAnalytics(
						createAnalyticsEvent({}),
						'mutateDailyBatchLocalTimePreference success',
						{
							value: optionValue,
						},
					);
				}
			},
		});
	};

	const selectedBatchWindowOption = batchWindowOptions.find(
		(option: optionBatchWindow) => option.value === data.batchWindow,
	);

	const selectedDailyBatchLocalTimeOption = data.dailyBatchLocalTime
		? data.dailyBatchLocalTime
		: '09:00';

	return (
		<>
			<Inline testId="personal-settings-preference-fields.common.ui.batch-window.select">
				<Box xcss={selectWrapperStyles}>
					<Select
						label={formatMessage(messages.selectBatchWindowLabel)}
						labelId="select-batch-notification-duration-label"
						inputId="batch-window"
						options={batchWindowOptions}
						value={selectedBatchWindowOption}
						onChange={(option) => {
							if (option) {
								commit({
									variables: {
										cloudId,
										input: {
											batchWindow: option.value,
											dailyBatchLocalTime: option.value === 'ONCE_PER_DAY' ? '09:00' : null,
										},
									},
									optimisticResponse: {
										jira: {
											updateGlobalNotificationOptions: {
												success: true,
												errors: [],
												options: {
													id: data.id,
													batchWindow: option.value,
													dailyBatchLocalTime: option.value === 'ONCE_PER_DAY' ? '09:00' : null,
												},
											},
										},
									},
									onError: (error) => {
										showErrorFlag();
										fireErrorAnalytics({
											meta: {
												id: 'mutateBatchWindowPreference',
												packageName: 'jiraPersonalSettingsPreferenceFields',
												teamName: 'jira-notifications-platform',
											},
											error,
											sendToPrivacyUnsafeSplunk: true,
										});
									},
									onCompleted: (response) => {
										const errors = response?.jira?.updateGlobalNotificationOptions?.errors;
										if (errors && errors?.length > 0) {
											showErrorFlag();
											errors.forEach((error) => {
												fireErrorAnalytics({
													meta: {
														id: 'mutateBatchWindowPreferenceCompletedError',
														packageName: 'jiraPersonalSettingsPreferenceFields',
														teamName: 'jira-notifications-platform',
													},
													error: new Error(error?.message || 'Unknown Error'),
													sendToPrivacyUnsafeSplunk: true,
												});
											});
										} else {
											fireTrackAnalytics(
												createAnalyticsEvent({}),
												'batchWindow changed',
												'batchWindow',
												{
													value: option.value,
												},
											);
											fireOperationalAnalytics(
												createAnalyticsEvent({}),
												'mutateBatchWindowPreference success',
												{
													value: option.value,
												},
											);
										}
									},
								});
							}
						}}
					/>
				</Box>
				{data.batchWindow === 'ONCE_PER_DAY' && (
					<Box xcss={selectWrapperStyles}>
						<TimePicker
							label={formatMessage(messages.timePickerLabel)}
							timeIsEditable
							value={selectedDailyBatchLocalTimeOption}
							selectProps={{
								isClearable: false,
							}}
							onChange={handleDailyBatchLocalTimeChange}
							id="once-per-day-scheduling-time"
						/>
					</Box>
				)}
			</Inline>
		</>
	);
};

const selectWrapperStyles = xcss({
	maxWidth: '260px',
	flex: 1,
	display: 'flex',
	flexDirection: 'column',
	marginRight: 'space.100',
});
